// src/lib/managers/api/types/organization.api.ts
// Miroir des DTOs backend — infra/rest/organization/types/

import { ActiveLicense } from "./license.api";

// ═══════════════════════════════════════════════════════════════════════
// Enums
// ═══════════════════════════════════════════════════════════════════════

enum OrganizationStatus {
    ACTIVE = "Active",
    HOLD = "Hold",
    DEACTIVATED = "Deactivated",
}

export enum LicenseAffectationStatus {
    ACTIVE = "ACTIVE",
}

enum HoldDetail {
    BrokenCgu = "BrokenCgu",
    EmptyAccount = "EmptyAccount",
}

enum BillingType {
    Direct = "DIRECT",
    Indirect = "INDIRECT",
    Stores = "STORES",
}

// ═══════════════════════════════════════════════════════════════════════
// Shared DTOs (used across multiple responses)
// ═══════════════════════════════════════════════════════════════════════

type OrganizationStatusDetail = {
    status: string;
    activeDate: string | null;
    holdDate: string | null;
    holdDetail: string | null;
    deactivatedDate: string | null;
};

export type LicenseAffectation = {
    instanceId: string;
    status: LicenseAffectationStatus;
    userMail: string | null;
};

export type OrganizationLicense = {
    id: string;
    licenseId: string;
    licenseCount: number;
    billingType: string;
    startDate: string;
    expirationDate: string | null;
    licensesAffectation: LicenseAffectation[];
};

export type OrganizationResponse = {
    id: string;
    name: string;
    externalSignatureManagement: boolean;
    statusDetail: OrganizationStatusDetail;
    organizationLicenses: OrganizationLicense[];
    organizationAdmins: string[];
};

// ═══════════════════════════════════════════════════════════════════════
// Request bodies
// ═══════════════════════════════════════════════════════════════════════

export type CreateOrganizationRequestBody = {
    name: string;
    externalSignatureManagement: boolean;
    organizationLicenses?: AddLicensesToOrganizationRequest[];
    organizationAdmins: string[];
};

export type UpdateOrganizationRequestBody = {
    name?: string;
    externalSignatureManagement?: boolean;
    status?: OrganizationStatus;
    holdDetail?: HoldDetail;
};

export type AddLicensesToOrganizationRequest = {
    licenseId: string;
    licenseCount: number;
    billingType: BillingType;
    startDate: string;
    expirationDate: string | null;
};

export type SetOrganizationLicenseExpirationDateRequest = {
    expirationDate: string;
};

// ═══════════════════════════════════════════════════════════════════════
// Response types
// ═══════════════════════════════════════════════════════════════════════

export type DeleteOrganizationResponse = {
    success: boolean;
};

export type OrganizationUser = {
    userId: string;
    displayName: string;
    surname: string;
    givenName: string;
    mail: string;
    activeLicense: ActiveLicense | null;
    isMonthlyAnalyzedMailLimitReached: boolean;
};

export type ListAllUsersAndLicenceResponse = OrganizationUser[];

// ═══════════════════════════════════════════════════════════════════════
// Snapshot post-mutation licence
// ═══════════════════════════════════════════════════════════════════════

/**
 * Ligne utilisateur impactée par une mutation licence — sous-ensemble de OrganizationUser
 * contenant les seuls champs susceptibles de changer. Permet de patcher la ligne du cache
 * (getAllUsersFromTenant) sans refetch.
 */
type AffectedUser = {
    userMail: string;
    activeLicense: ActiveLicense | null;
    isMonthlyAnalyzedMailLimitReached: boolean;
};

/**
 * Snapshot cohérent renvoyé par les mutations licence (assign / reassign / unassign) :
 * organisation à jour (= même shape que getTeamLicenseData) + ligne user impactée.
 * Permet un setQueryData propre côté consommateur, sans refetch désynchronisé.
 */
export type LicenseMutationSnapshot = {
    organization: OrganizationResponse;
    affectedUser: AffectedUser;
};

// ═══════════════════════════════════════════════════════════════════════
// Assign B2B User to Organization License
// ═══════════════════════════════════════════════════════════════════════

export type AssignOrgLicenseToUserRequest = {
    userMail: string;
    orgLicenseId: string;
};

export type AssignOrgLicenseToUserResponse = {
    instanceId: string;
    userMail: string;
    isFirstB2BLicense: boolean;
} & LicenseMutationSnapshot;

/** Réponse normalisée retournée par affectTeamLicenseToUsers(), qu'il s'agisse d'un assign ou d'un reassign. */
export type AffectOrgLicenseToUserResponse = {
    instanceId: string;
    userMail: string;
    isReassign: boolean;
} & LicenseMutationSnapshot;

// ═══════════════════════════════════════════════════════════════════════
// Remove B2B User from Organization License
// ═══════════════════════════════════════════════════════════════════════

export type RemoveOrgLicenseFromB2BUserRequest = {
    userMail: string;
    orgLicenseId: string;
};

export type RemoveOrgLicenseFromB2BUserResponse = {
    userMail: string;
    hadConnected: boolean;
} & LicenseMutationSnapshot;

// ═══════════════════════════════════════════════════════════════════════
// Modify (Reassign) Organization License for B2B User
// ═══════════════════════════════════════════════════════════════════════

export type ModifyOrgLicenseForB2BUserRequest = {
    userMail: string;
    newOrgLicenseId: string;
};

export type ModifyOrgLicenseForB2BUserResponse = {
    oldInstanceId: string | null;
    newInstanceId: string;
    userMail: string;
    hadConnected: boolean;
} & LicenseMutationSnapshot;

// ═══════════════════════════════════════════════════════════════════════
// Update User Admin Role
// ═══════════════════════════════════════════════════════════════════════

export type UpdateUserAdminRoleRequest = {
    userId: string;
    userMail: string;
    setAdmin: boolean;
};

export type UpdateUserAdminRoleResponse = {
    userId: string;
    userMail: string;
    isAdmin: boolean;
    tokenRefreshRequired: boolean;
    // Snapshot post-mutation : organisation à jour (organizationAdmins) → setQueryData sans refetch
    organization: OrganizationResponse;
};
